import { DomainMessagesTypes, Services } from 'shared/domain/messages/message';
import { subscribeToAnySignal } from 'serviceWorker/events/swSignal';
import { swSignals } from 'serviceWorker/events/swSignals';
import type { ProcessType } from 'shared/domain/process/types/model';
import { broadcastAllProcesses } from 'serviceWorker/broadcasts/processes';
import { DataReplacedError } from 'shared/channels/cancellation';
import { RESTARTS_FOR_REPLACED_DATA } from 'serviceWorker/const/dataReplacement';
import { debounce } from 'serviceWorker/helpers/debounce';
import { createAbortOnLogout } from 'serviceWorker/helpers/abortableServiceHandler';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { broadcastPullFailed } from 'serviceWorker/helpers/broadcastPullError';
import { getEntityFailureLevel } from 'serviceWorker/helpers/entityFailureLog';
import { getPullDiagnostics } from 'serviceWorker/helpers/pullDiagnostics';
import {
  createAuthRetryBudget,
  hasScheduledAuthCooldownRetry,
  scheduleAfterAuthCooldown,
} from 'serviceWorker/helpers/scheduleSwRetry';
import { debugLog } from 'shared/logger/debugLog';
import { LogLevel } from 'shared/types/logger';
import { ensureAuthenticatedConfig } from '../config';
import { fetchProcesses } from './fetchProcesses';
import {
  isProcessesConfigReady,
  isProcessesConfigWithoutProject,
  waitForConfig,
} from './waitForConfig';
import { processContract } from 'shared/domain/process/processContract';
export {
  handleUpdateProcessWorkflow,
  type UpdateProcessWorkflowPayload,
} from './updateProcessWorkflow';
import {
  clearCachedProcesses,
  getCachedProcesses,
  hasCachedProcessesSnapshot,
  setCachedProcesses,
} from './cache';

let lastProjectId: string | null = null;
let abortManager: ReturnType<typeof createAbortOnLogout> | null = null;
let scheduleRefresh: (() => void) | null = null;

/**
 * One download at a time: concurrent reads wait for the same one instead of
 * getting an incomplete list. Clearing the cache (project switch, logout)
 * starts a new generation, so a download of the previous project neither
 * answers new reads nor gets cached.
 */
let pendingLoad: Promise<ProcessType[]> | null = null;
let cacheGeneration = 0;

// Same recovery as makePullHandler: after an auth cooldown, pull again.
const AUTH_RETRY_KEY = 'pull:processes';
const authRetryBudget = createAuthRetryBudget();

interface ProcessesDownload {
  processes: ProcessType[];
  /** False before a project is selected: the empty list is complete, but not worth caching. */
  isProjectSelected: boolean;
}

/** A lost network or a project without access is logged as info, as for the other entities. */
async function reportProcessesPullFailed(
  reason: string,
  error: unknown,
  level: LogLevel = getEntityFailureLevel(error)
): Promise<void> {
  swLog(
    `[processes] Pull failed: ${reason}`,
    level,
    error,
    { entityName: Services.PROCESSES, setup: getPullDiagnostics() }
  );
  await broadcastPullFailed({
    channelContract: processContract,
    entityName: Services.PROCESSES,
    error: error ?? new Error(reason),
  });
}

async function reportConfigUnavailable(): Promise<void> {
  scheduleAfterAuthCooldown(
    AUTH_RETRY_KEY,
    () => {
      void pullProcessesHandler();
    },
    authRetryBudget
  );
  if (hasScheduledAuthCooldownRetry(AUTH_RETRY_KEY)) {
    // The retry broadcasts the result once the cooldown ends.
    debugLog('[processes] Pull deferred: fetch config unavailable');
    return;
  }
  await reportProcessesPullFailed('fetch config unavailable', null, LogLevel.INFO);
}

/** Downloads the processes of the selected project; rejects when it cannot. */
async function downloadProcesses(
  abortController: AbortController
): Promise<ProcessesDownload> {
  const config = await waitForConfig();

  if (isProcessesConfigWithoutProject(config)) {
    // Nothing to fetch before a project is selected. Selecting a project
    // emits `swSignals.configApplied`, which pulls the real list.
    debugLog('[processes] No project selected, answering an empty list');
    return { processes: [], isProjectSelected: false };
  }

  if (!isProcessesConfigReady(config)) {
    debugLog('[processes] Config not available after retries');
    await reportConfigUnavailable();
    throw new Error('Processes fetch config unavailable');
  }

  authRetryBudget.attempts = 0;
  try {
    debugLog('[processes] Fetching processes for project:', config.setup.projectId);
    const processes = await fetchProcesses(config.setup, abortController);
    // A logout or a project switch aborted it: a cancellation, not a failure.
    abortController.signal.throwIfAborted();
    if (!processes) {
      throw new Error('Processes download skipped: the config has no API or project');
    }
    debugLog('[processes] Fetched', processes.length, 'processes');
    return { processes, isProjectSelected: true };
  } catch (error: unknown) {
    if (!abortController.signal.aborted) {
      await reportProcessesPullFailed('fetch error', error);
    }
    throw error;
  }
}

/**
 * Downloads, caches and broadcasts the processes. On failure the cached list
 * is broadcast as it is (readers waiting for the collection get an answer) and the promise rejects.
 */
async function runLoad(generation: number): Promise<ProcessType[]> {
  if (!abortManager) {
    throw new Error('Processes service is not initialized');
  }
  const cachedProcesses = getCachedProcesses();
  const hasCompleteSnapshot = hasCachedProcessesSnapshot();
  try {
    const { processes, isProjectSelected } = await downloadProcesses(abortManager.createController());
    if (generation !== cacheGeneration) {
      throw new DataReplacedError();
    }
    if (isProjectSelected) {
      setCachedProcesses(processes);
    }
    broadcastAllProcesses(processes, true);
    return processes;
  } catch (error: unknown) {
    if (generation === cacheGeneration) {
      broadcastAllProcesses(cachedProcesses, hasCompleteSnapshot);
    }
    throw error;
  }
}

function loadProcesses(): Promise<ProcessType[]> {
  if (pendingLoad) {
    return pendingLoad;
  }
  const load = runLoad(cacheGeneration).finally(() => {
    if (pendingLoad === load) {
      pendingLoad = null;
    }
  });
  pendingLoad = load;
  return load;
}

/** Background refresh: answers by broadcast only. */
const pullProcessesHandler = async (): Promise<void> => {
  if (pendingLoad || !abortManager) {
    broadcastAllProcesses(getCachedProcesses(), hasCachedProcessesSnapshot());
    return;
  }
  await loadProcesses().catch(() => undefined);
};

/**
 * A load that a project switch replaced (a new cache generation began while it
 * ran, which also aborts its download) is not the answer for a read of the
 * current project: the read goes on with the load of the current one, for a
 * few restarts. A failure of a load nothing replaced is the read's failure.
 */
async function loadProcessesOfCurrentProject(): Promise<ProcessType[]> {
  for (let restarts = 0; ; restarts += 1) {
    const generation = cacheGeneration;
    try {
      return await loadProcesses();
    } catch (error: unknown) {
      const isReplaced = generation !== cacheGeneration;
      if (!isReplaced || restarts >= RESTARTS_FOR_REPLACED_DATA) {
        throw error;
      }
    }
  }
}

/**
 * The processes of the selected project: the cached ones (refreshed in the
 * background), otherwise once downloaded. Rejects when the download fails.
 */
export function getAllProcesses(): Promise<ProcessType[]> {
  if (hasCachedProcessesSnapshot()) {
    scheduleRefresh?.();
    return Promise.resolve(getCachedProcesses());
  }
  return loadProcessesOfCurrentProject();
}

/** Downloads the processes again (debounced); the result is broadcast. */
export function syncProcesses(): void {
  scheduleRefresh?.();
}

/** Keeps the cache in line with a change the server accepted. */
export function replaceCachedProcess(process: ProcessType): void {
  if (!hasCachedProcessesSnapshot()) {
    return;
  }
  setCachedProcesses(
    getCachedProcesses().map((cached) => (cached._id === process._id ? process : cached))
  );
}

export function clearProcessesCache(): void {
  cacheGeneration += 1;
  pendingLoad = null;
  clearCachedProcesses();
}

export function getProcesses(): ProcessType[] {
  return getCachedProcesses();
}

export const init = (): void => {
  abortManager = createAbortOnLogout();
  const debounced = debounce(pullProcessesHandler, 250);
  scheduleRefresh = debounced;

  subscribeToAnySignal([swSignals.loggedOut, swSignals.projectDataReplaced], clearProcessesCache);

  const onProcessesRefreshSignal = async (): Promise<void> => {
    const setup = await ensureAuthenticatedConfig();
    const newProjectId = setup?.projectId ?? null;

    if (!newProjectId) {
      return;
    }

    const projectChanged = newProjectId !== lastProjectId;
    if (projectChanged) {
      lastProjectId = newProjectId;
      clearProcessesCache();
    }

    if (projectChanged || getCachedProcesses().length === 0) {
      debounced();
    }
  };

  // A config / project switch, or a table flush after INVALID_SYNCKEY (PT-6894).
  subscribeToAnySignal([swSignals.configApplied, swSignals.syncKeyInvalidated], onProcessesRefreshSignal);
};
